import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
// // import { AutofocusFixModule } from 'ngx-autofocus-fix';
import { AvatarModule } from 'ngx-avatars';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { WidgetLoadingSpinnerComponent } from '@platform/components/widget-loading-spinner/widget-loading-spinner.component';
import { A11yModule } from '@angular/cdk/a11y';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatChipsModule } from '@angular/material/chips';
import { TypeSafeMatRowDefDirective } from '@platform/table/type-safe-mat-row-def.directive';
import { FormToggleFilterUsersByCustomerSpaceComponent } from '@core/create-user/form-toggle-filter-users-by-customer-space/form-toggle-filter-users-by-customer-space.component';
import { ShowAuthorizeDirective } from './auth/show-authorize.directive';
import { DragAndDropInfoComponent } from './components/file-upload-widget/drag-and-drop-info/drag-and-drop-info.component';
import { DragAndDropDirective } from './components/file-upload-widget/drag-and-drop/drag-and-drop.directive';
import { ProfilePictureUploadDialogComponent } from './components/file-upload-widget/file-upload/profile-picture-upload-dialog/profile-picture-upload-dialog.component';
import { FileUploadComponent } from './components/file-upload-widget/file-upload/file-upload.component';
import { GlobalInfoComponent } from './components/global-info/global-info.component';
import { InfoDialogComponent } from './components/info-dialog/info-dialog.component';
import { LinkButtonComponent } from './components/buttons/link-button/link-button.component';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import { PlatformEffects } from './platform.effects';
import { platformReducer } from './platform.reducers';
import { PLATFORM_STATE_FEATURE_KEY } from './platform.state';
import { RouterEffects } from './router.effects';
import { CustomIconService } from './shared/custom-icon.service';
import { StepperComponent } from './components/stepper/stepper.component';
import { StepComponent } from './components/stepper/step/step.component';
import { RotateChevronComponent } from './components/rotate-chevron/rotate-chevron.component';
import { UpdateUserRoleDialogComponent } from './components/update-user-role-dialog/update-user-role-dialog.component';
import { CalloutComponent } from './components/callout/callout.component';
import { NewIndicatorComponent } from './components/new-indicator/new-indicator.component';
import { ButtonDeleteComponent } from './components/buttons/button-delete/button-delete.component';
import { ButtonEditComponent } from './components/buttons/button-edit/button-edit.component';
import { ExperTableComponent } from './components/exper-table/exper-table.component';
import { ExperColumnComponent } from './components/exper-table/exper-column.component';
import { ButtonCreateExternalUserComponent } from './components/button-create-external-user.component';
import { SpacerComponent } from './components/spacer.component';
import { ButtonCloseComponent } from './components/buttons/button-close.component';
import { AutofocusDirectiveModule } from './autofocus/autofocus.module';
import { FileUploadDialogComponent } from './components/file-upload-widget/file-upload/file-upload-dialog/file-upload-dialog.component';
import { DialogBaseComponent } from './components/dialog-base/dialog-base.component';
import { ToolbarActionsComponent } from './components/toolbar-actions/toolbar-actions.component';
import { ToolbarActionDropdownButtonComponent } from './components/toolbar-action-dropdown-button/toolbar-action-dropdown-button.component';
import { LoadingButtonDirective } from './components/buttons/loading-button/loading-button.directive';
import { PendingContainerComponent } from './components/pending-container/pending-container.component';
import { DrawerBaseComponent } from './components/drawer-base/drawer-base.component';
import { AutocompleteInputComponent } from './components/autocomplete-input/autocomplete-input.component';
import { AutocompleteMultiInputComponent } from './components/autocomplete-multi-input/autocomplete-multi-input.component';
import { LabeledContainerComponent } from './components/labeled-container/labeled-container.component';
import { CommentComponent } from './components/comment/comment/comment.component';
import { CommentCreateFormComponent } from './components/comment/comment-create-form/comment-create-form.component';
import { CommentScrollContainerComponent } from './components/comment/comment-scroll-container/comment-scroll-container.component';
import { FilenameWithIconComponent } from './components/filename-with-icon/filename-with-icon.component';
import { TypeSafeMatCellDefDirective } from './table/type-safe-mat-cell-def.directive';
import { UserSelectionDialogComponent } from './components/user-selection-dialog/user-selection-dialog.component';
import { ButtonSaveComponent } from './components/buttons/button-save.component';
import { ButtonCancelComponent } from './components/buttons/button-cancel.component';
import { ToastEffects } from './toast/toast.effects';
import { FileRenameDialogComponent } from './components/file-rename-dialog/file-rename-dialog.component';
import { HighlightPipe } from './shared/highlight.pipe';
import { EmptyListComponent } from './components/empty-list/empty-list.component';
import { LoadingSpinnerOverlayComponent } from './components/loading-spinner-overlay/loading-spinner-overlay.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';

@NgModule({
  declarations: [
    FileUploadComponent,
    ProfilePictureUploadDialogComponent,
    DragAndDropDirective,
    DragAndDropInfoComponent,
    InfoDialogComponent,
    GlobalInfoComponent,
    StepperComponent,
    StepComponent,
    RotateChevronComponent,
    UpdateUserRoleDialogComponent,
    CalloutComponent,
    WidgetLoadingSpinnerComponent,
    NewIndicatorComponent,
    ButtonDeleteComponent,
    ButtonEditComponent,
    ExperTableComponent,
    ExperColumnComponent,
    ButtonCreateExternalUserComponent,
    SpacerComponent,
    FileUploadDialogComponent,
    ToolbarActionsComponent,
    ToolbarActionDropdownButtonComponent,
    PendingContainerComponent,
    DrawerBaseComponent,
    AutocompleteInputComponent,
    AutocompleteMultiInputComponent,
    LabeledContainerComponent,
    CommentComponent,
    CommentCreateFormComponent,
    CommentScrollContainerComponent,
    FilenameWithIconComponent,
    TypeSafeMatCellDefDirective,
    TypeSafeMatRowDefDirective,
    UserSelectionDialogComponent,
    FileRenameDialogComponent,
    HighlightPipe,
  ],
  exports: [
    FileUploadComponent,
    DragAndDropDirective,
    DragAndDropInfoComponent,
    GlobalInfoComponent,
    StepperComponent,
    StepComponent,
    RotateChevronComponent,
    CalloutComponent,
    WidgetLoadingSpinnerComponent,
    NewIndicatorComponent,
    ButtonDeleteComponent,
    ButtonEditComponent,
    ExperTableComponent,
    ExperColumnComponent,
    ButtonCreateExternalUserComponent,
    SpacerComponent,
    DialogBaseComponent,
    ToolbarActionsComponent,
    ToolbarActionDropdownButtonComponent,
    PendingContainerComponent,
    DrawerBaseComponent,
    AutocompleteInputComponent,
    AutocompleteMultiInputComponent,
    LabeledContainerComponent,
    CommentCreateFormComponent,
    CommentComponent,
    CommentScrollContainerComponent,
    FilenameWithIconComponent,
    TypeSafeMatCellDefDirective,
    TypeSafeMatRowDefDirective,
    HighlightPipe,
  ],
  providers: [CustomIconService],
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatMenuModule,
    MatToolbarModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    AvatarModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatSelectModule,
    FormsModule,
    // AutofocusFixModule.forRoot(),
    StoreModule.forFeature(PLATFORM_STATE_FEATURE_KEY, platformReducer),
    EffectsModule.forFeature([PlatformEffects, RouterEffects, ToastEffects]),
    TranslateModule,
    MatTooltipModule,
    CdkStepperModule,
    MatSortModule,
    MatPaginatorModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    PageHeaderComponent,
    ButtonCloseComponent,
    LinkButtonComponent,
    ButtonSaveComponent,
    ButtonCancelComponent,
    AutofocusDirectiveModule,
    A11yModule,
    MatSidenavModule,
    MatChipsModule,
    FormToggleFilterUsersByCustomerSpaceComponent,
    DialogBaseComponent,
    EmptyListComponent,
    ShowAuthorizeDirective,
    LoadingButtonDirective,
    LoadingSpinnerComponent,
    LoadingSpinnerOverlayComponent,
  ],
})
export class PlatformModule {}
